import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { RelocationProcessApiAdapter } from '../adapters/RelocationProcessApiAdapter';
import type { ProductDetail } from '../models';

const PRODUCT_QUERY_KEY = 'relocation-process-product';

/**
 * Resuelve los productos referenciados por las líneas de una reubicación.
 * Usa GET /api/v1/products/{productId} por cada id único.
 * Cachea por product_id para evitar reconsultas al repetir producto.
 */
export const useProductDetails = (productIds: number[]) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new RelocationProcessApiAdapter(httpClient), [httpClient]);

  const uniqueIds = useMemo(() => {
    return Array.from(new Set(productIds.filter((id) => Number.isInteger(id) && id > 0)));
  }, [productIds]);

  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: [PRODUCT_QUERY_KEY, id],
      queryFn: () => adapter.getProductById(id),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });

  const productsById = useMemo(() => {
    const map = new Map<number, ProductDetail>();
    queries.forEach((q, idx) => {
      const id = uniqueIds[idx];
      if (q.data) map.set(id, q.data);
    });
    return map;
  }, [queries, uniqueIds]);

  const isLoading = queries.some((q) => q.isLoading);

  return { productsById, isLoading };
};
